import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Save, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Profile } from "@/hooks/useProfile";
import { ProfileEditTabs } from './ProfileEditTabs';

interface ProfileEditFormProps {
  profile: Profile;
  editedProfile: Profile;
  setEditedProfile: (profile: Profile) => void;
  onCancel: () => void;
  onSave: () => void;
  avatarUploading: boolean;
  onAvatarUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ProfileEditForm = ({
  profile,
  editedProfile,
  setEditedProfile,
  onCancel,
  onSave,
  avatarUploading,
  onAvatarUpload
}: ProfileEditFormProps) => {
  
  const handleSaveProfile = async () => {
    if (!profile?.user_id || !editedProfile) return;

    try {
      // If hardiness_zone_id is set, sync the usda_zone text field
      let usdaZoneText = editedProfile.usda_zone;
      if (editedProfile.hardiness_zone_id && !usdaZoneText) {
        try {
          const { data: zoneData } = await supabase
            .from('hardiness_zones')
            .select('zone_code')
            .eq('id', editedProfile.hardiness_zone_id)
            .single();
          
          if (zoneData?.zone_code) {
            usdaZoneText = zoneData.zone_code;
          }
        } catch (error) {
          console.error('Error fetching zone code:', error);
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({
          username: editedProfile.username,
          farm_name: editedProfile.farm_name,
          bio: editedProfile.bio,
          location: editedProfile.location,
          usda_zone: usdaZoneText,
          hardiness_zone_id: editedProfile.hardiness_zone_id,
          farm_website: editedProfile.farm_website,
          farming_since: editedProfile.farming_since,
          farm_size: editedProfile.farm_size,
          farm_goals: editedProfile.farm_goals,
          farm_images: editedProfile.farm_images,
          avatar_url: editedProfile.avatar_url,
          farm_logo_url: editedProfile.farm_logo_url,
          is_discoverable: editedProfile.is_discoverable,
          location_visible: editedProfile.location_visible,
          profile_tags: editedProfile.profile_tags,
          is_active: editedProfile.is_active,
        })
        .eq('user_id', profile.user_id);

      if (error) throw error;

      toast.success("Profile updated successfully!");
      onSave();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error("Failed to update profile");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Edit Profile</CardTitle>
            <Button onClick={handleSaveProfile} className="flex items-center gap-2">
              <Save size={16} />
              Save Changes
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <ProfileEditTabs
                profile={profile}
                editedProfile={editedProfile}
                setEditedProfile={setEditedProfile}
                avatarUploading={avatarUploading}
                onAvatarUpload={onAvatarUpload}
              />
              
              <div className="flex gap-2 pt-4">
                <Button onClick={handleSaveProfile} className="flex items-center gap-2">
                  <Save size={16} />
                  Save Changes
                </Button>
                <Button 
                  onClick={onCancel} 
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <X size={16} />
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};